import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { V2Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import { InformationalDrawer } from './common/InformationalDrawer';
import { getChipValue, getLobForPortalData, toSlug } from './Guest.utils';
import { InfoButtonStyle } from './GuestPlanSelection.style';
import { getBlueGreenGQLApiUrl } from './utils';

const TypeOfCareCardStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '180px',
    height: '343px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
});

const TypeOfCareCard = (
  title: string,
  analyticsTitle: string,
  description: string,
  icon: string,
  linkText: string,
  coverageType: string,
  lob: string
) => {
  const chipValue = getChipValue(coverageType);
  const { navigate } = useRouter();
  const coverageLinks = useLagoon('coverage-links')();

  const handleCardClick = () => {
    sessionStorage.setItem('guestLocation', '');

    const matchedCoverageLink = coverageLinks.find(
      (item) => item?.coverage === coverageType && item?.lob === lob
    )?.link;

    adobeLinkTrackEvent({
      name: linkText,
      location: analyticsTitle,
      type: 'internal',
      destinationUrl: matchedCoverageLink
        ? matchedCoverageLink
        : `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`,
    });

    if (matchedCoverageLink) {
      window.open(matchedCoverageLink, '_blank');
    } else {
      navigate(
        `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`
      );
    }
  };
  return (
    <TypeOfCareCardStyledCard key={coverageType}>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '0px',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '24px',
              alignItems: 'start',
              marginTop: '0px',
              marginBottom: '0px',
              '& > :first-child': {
                margin: '0',
                height: '48px',
                width: '48px',
              },
            },
          }}
        >
          <IconBrand icon={icon} size="48px" />
          <Heading css={{ marginTop: '16px' }} offset={5}>
            {title}
          </Heading>
          <Text
            css={{
              'abyss-text-root': {
                display: 'inline-block',
                lineHeight: '1.3 !important',
              },
            }}
            size={'$sm'}
          >
            {description}
          </Text>
        </Layout.Stack>
      </Card.Section>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '16px',
            height: '52px',
            width: '180px',
            marginLeft: '0px',
            marginRight: '0px',
          },
        }}
      >
        <Layout.Group>
          <Link
            after={<IconSymbol icon="chevron_right" size="16" />}
            data-testid={`explore-${toSlug(title)}-link`} // Use the utility function
            isStandardAnchor
            onClick={handleCardClick}
            size={'sm'}
          >
            {linkText}
          </Link>
        </Layout.Group>
      </Card.Section>
    </TypeOfCareCardStyledCard>
  );
};

export const GuestTypeOfCarePicker = () => {
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const lob = searchParams?.get('planSelectionLob') || '';
  const coverageType =
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.COVERAGE_TYPE) || '';
  const isGuestDeeplink =
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.SOURCE) ===
    'deeplink';
  const coverageLinks = useLagoon('coverage-links')();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lagoonData = useLagoon('coverage')();

  const lagoonUIMessage = useLagoon('ui-messaging')();

  const featureFlags = useLagoon('feature-flags')();
  const { t } = useTranslation();

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const chooseTypeOfCareText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_TYPE_OF_CARE,
  })?.message;
  const typeOfCareHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_CARE_HEADING,
  })?.message;
  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message;
  // Retrieve the sign-in URL saved by GuestHeader
  const signInUrl = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_SIGN_IN_URL
  );
  const exploreText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.EXPLORE,
  })?.message;

  const bannerText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .TYPE_OF_CARE_PICKER_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const drawerHeaderText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_CARE_PICKER_DRAWER_HEADER,
  })?.message;

  const drawerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .TYPE_OF_CARE_PICKER_DRAWER_CONTENT,
  })?.message;

  const isFirstTimeUserBannerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED
  );

  const handleOnLearnMoreClick = () => {
    setIsDrawerOpen(true);
  };

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const [shouldShowFirstTimeUserBanner, setShouldShowFirstTimeUserBanner] =
    useState(false);

  const [saveEducationalContent, setEducationalContentStatus] =
    useSaveEducationalContentStatus({
      url: guestBGApiUrl,
    });

  const handleOnClose = () => {
    setShouldShowFirstTimeUserBanner(false);
    setEducationalContentStore(
      saveEducationalContent.data.saveEducationalContentStatus
    );
  };

  const { chooseType, setChooseType } = useGuestStore(
    useShallow((state) => ({
      chooseType: (state as GuestState).chooseType,
      setChooseType: (state as GuestState).setChooseType,
    }))
  );

  useEffect(() => {
    if (
      educationalContentStore?.hasViewedGuestCoverageTypeBanner === false &&
      isFirstTimeUserBannerEnabled
    ) {
      setShouldShowFirstTimeUserBanner(true);
      setEducationalContentStatus({
        variables: {
          ...educationalContentStore,
          hasViewedContent: educationalContentStore?.hasViewedContent ?? true,
          hasViewedGuestCoverageTypeBanner: true,
        },
      });
    }
  }, [educationalContentStore?.hasViewedGuestCoverageTypeBanner]);

  useEffect(() => {
    if (chooseTypeOfCareText) {
      setChooseType(chooseTypeOfCareText);
    }
  }, [chooseTypeOfCareText, setChooseType]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: `select coverage type`,
    isGuest: true,
    sitesectionLevel1: 'guest plan selection',
    guestLob: getLobForPortalData(lob),
  });

  useEffect(() => {
    adobePageTrackEvent();
    if (isGuestDeeplink && lob && coverageType) {
      const matchedCoverageLink = coverageLinks.find(
        (item) => item?.coverage === coverageType && item?.lob === lob
      )?.link;
      if (matchedCoverageLink) {
        window.open(matchedCoverageLink, '_blank');
      }
    }
  }, []);

  return (
    <React.Fragment>
      {shouldShowFirstTimeUserBanner && (
        <FirstTimeUserBanner
          content={bannerText}
          onClose={handleOnClose}
          onLearnMoreClick={handleOnLearnMoreClick}
        />
      )}
      <Flex
        alignContent="center"
        css={{ marginTop: '32px', gap: '6px' }}
        direction="row"
        justify="center"
      >
        <Heading
          data-testid="type-of-care-selection"
          offset={3}
          textAlign="center"
        >
          {typeOfCareHeadingText}
        </Heading>
        <V2Button
          aria-label={t('MORE_INFORMATION_ABOUT_CARE_TYPE')}
          css={InfoButtonStyle}
          data-testid="info-button"
          onClick={() => setIsDrawerOpen(true)}
          style={{ alignSelf: 'center' }}
          variant="text"
        >
          <IconSymbol icon="info" size={16} variant="outlined" />
        </V2Button>
      </Flex>
      <Flex
        alignItems="baseline"
        css={{ marginTop: '8px' }}
        direction="row"
        justify="center"
      >
        <Text>{signInMessageText}</Text>
        <Link
          data-testid={`sign-in-link`}
          href={signInUrl}
          onClick={() => {
            adobeLinkTrackEvent({
              name: signInText,
              location: chooseType,
              type: 'internal',
              destinationUrl: signInUrl,
            });
          }}
          openNewWindow={false}
          style={{ marginLeft: '4px' }}
        >
          {signInText}
        </Link>
      </Flex>

      <Flex
        alignItems="baseline"
        direction="row"
        justify="center"
        style={{ gap: '24px', marginTop: '32px' }}
      >
        {lagoonData.map((item) => {
          if (
            item.dntLobs?.toLowerCase().includes(lob.toLocaleLowerCase()) ||
            item.dntLobs.includes('all')
          ) {
            return TypeOfCareCard(
              item.displayName,
              item.dnt_name,
              item.description,
              item.dntIcon,
              exploreText,
              item.key,
              lob
            );
          }
          return null;
        })}
      </Flex>
      <InformationalDrawer
        content={drawerContentText}
        header={drawerHeaderText}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
      />
    </React.Fragment>
  );
};
